import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import Select from "react-select";
import configurationAPI from "../../apis/configuration/configurationAPI";

function AddChartInfoFieldModal({ onHide, show, selectedLoc, selectedClient, setInvalidateChartInfoFields }) {
    const {
        handleSubmit,
        watch,
        register,
        control,
        setValue,
        reset,
        formState: { errors },
        unregister,
    } = useForm({
        defaultValues: {
            isMultiSelect: { value: false, label: 'No' },
            validation: { value: "Mandatory", label: 'Mandatory' },
            placement: { value: "Chart Info", label: 'Chart Info' },
            isSpecialCharAllowed: { value: false, label: 'No' },
        }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "dropdown",
        shouldUnregister: true
    });

    const onSubmit = async (data) => {
        try {
            const reqBody = {
                name: data?.name,
                type: data?.type?.value,
                isMultiSelect: data?.isMultiSelect?.value || false,
                options: data?.dropdown?.map((item) => item?.name),
                validation: data?.validation?.value,
                placement: data?.placement?.value,
                isSpecialCharAllowed: data?.isSpecialCharAllowed?.value || false,
            }
            const response = await configurationAPI.addChartInfoCustomFields(reqBody, selectedClient, selectedLoc);
            if (response?.success) {
                window.toastr.success(response?.message);
                setInvalidateChartInfoFields(prev => !prev);
                handleHideModal();
            } else {
                window.toastr.error(response?.message);
            }
        } catch (error) {
            console.log(error);
        }

    }

    const handleHideModal = () => {
        onHide();
        reset({
            name: '',
            type: '',
            isMultiSelect: '',
            dropdown: '',
            validation: '',
            placement: '',
            isSpecialCharAllowed: '',
        });
    }

    const renderBoolean = () => ([
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
    ]);

    const renderValidation = () => ([
        { value: "Mandatory", label: 'Mandatory' },
        { value: "Non-Mandatory", label: "Non-Mandatory" },
        { value: "Not-Applicable", label: "Not-Applicable" },
    ]);

    const renderPlacement = () => ([
        { value: "Chart Info", label: 'Chart Info' },
        { value: "Processing Info", label: "Processing Info" },
    ]);

    const renderTypes = () => ([
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
        { value: 'dropdown', label: 'Dropdown' },
    ]);

    useEffect(() => {
        if (watch('type')?.value === 'dropdown') {
            append({ name: '' });
            setValue('isMultiSelect', { value: false, label: 'No' });
        }
        else {
            remove();
            unregister('dropdown');
            unregister('isMultiSelect');
        }

        // eslint-disable-next-line
    }, [watch('type')?.value]);

    return (
        <Modal show={show} onHide={handleHideModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Chart Info Field</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form className="row" onSubmit={handleSubmit(onSubmit)}>
                    <div className="col-6">
                        <label className="form-label required">Name</label>
                        <input
                            type="text"
                            className="form-control"
                            {...register("name", { required: { value: true, message: 'Required' } })}
                        />
                        {
                            errors.name && <p className="text-danger fs-7">{errors.name.message}</p>
                        }
                    </div>

                    <div className="col-6">
                        <label className="form-label required">Type</label>
                        <Controller
                            name="type"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderTypes()}
                                />
                            )}
                        />
                        {
                            errors.type && <p className="text-danger fs-7">{errors.type.message}</p>
                        }
                    </div>

                    {watch('type')?.value === 'dropdown' && <div className="col-12 mt-4">
                        <label className="form-label required">Is Multi Select</label>
                        <Controller
                            name="isMultiSelect"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    value={value}
                                    onChange={(value) => onChange(value)}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderBoolean()}
                                    defaultValue={{ value: false, label: 'No' }}
                                />
                            )}
                        />
                        {
                            errors.isMultiSelect && <p className="text-danger fs-7">{errors.isMultiSelect.message}</p>
                        }
                    </div>}

                    <div className="col-6 mt-4 mb-2">
                        <label className="form-label required">Validation</label>
                        <Controller
                            name="validation"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderValidation()}
                                />
                            )}
                        />
                        {
                            errors.validation && <p className="text-danger fs-7">{errors.validation.message}</p>
                        }
                    </div>

                    <div className="col-6 mt-4 mb-2">
                        <label className="form-label required">Placement</label>
                        <Controller
                            name="placement"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderPlacement()}
                                />
                            )}
                        />
                        {
                            errors.placement && <p className="text-danger fs-7">{errors.placement.message}</p>
                        }
                    </div>

                    {watch('type')?.value === 'dropdown' && <div className="col gap-3">
                        <div className="col-6 mt-4 mb-2">
                            <button
                                type="button"
                                onClick={() => append({ name: '' })} className="btn btn-secondary btn-sm form-control d-flex gap-2 align-items-center justify-content-center"
                            >
                                Add Options
                                <i className="fa fa-plus" />
                            </button>
                        </div>

                        {fields.length>0 && fields?.map((field, index) => (
                            <div key={field.id} className="row">
                                <div className="mt-1 form-group d-flex flex-wrap align-items-center gap-2">
                                    <input
                                        type="text"
                                        className="form-control mw-100 w-75 mb-1"
                                        {...register(`dropdown.${index}.name`, { required: { value: true, message: 'Required' } })}
                                    />
                                    <button
                                        className="btn btn-sm btn-icon btn-light-danger"
                                        onClick={() => remove(index)}
                                        type="button"
                                    >
                                        x
                                    </button>
                                </div>
                                {errors?.dropdown?.[index]?.name && <p className="text-danger fs-7">{errors?.dropdown?.[index]?.name?.message}</p>}
                            </div>))
                        }
                    </div>}

                    {watch('type')?.value === 'number' && <div className="col-6 mt-4 mb-2">
                        <label className="form-label">Allow Special Characters</label>
                        <Controller
                            name="isSpecialCharAllowed"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderBoolean()}
                                    defaultValue={{ value: false, label: 'No' }}
                                />
                            )}
                        />
                    </div>}

                    <div className="d-flex gap-3 align-items-center justify-content-end mt-4" >
                        <button
                            type="button"
                            className="btn btn-light mt-4 btn-sm"
                            onClick={handleHideModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary mt-4 btn-sm"
                        >
                            Save
                        </button>
                    </div>

                </form>
            </Modal.Body>
        </Modal>
    )
}

export default AddChartInfoFieldModal